/**
 * Google Maps loader — Airbnb-style singleton with aggressive prewarm.
 *
 * Strategy:
 *   1. In-memory + sessionStorage + localStorage cache for the API key
 *      (key is referrer-restricted, safe to persist on the device).
 *   2. Single in-flight Promise so concurrent callers share one network call.
 *   3. Preload the Google Maps JS script as soon as the key is known so
 *      `useJsApiLoader` resolves instantly when a map component mounts.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Libraries } from "@react-google-maps/api";

const SESSION_KEY = "berastays_gmaps_key";
const LOCAL_KEY = "berastays_gmaps_key_v1";
const LOCAL_TTL_MS = 24 * 60 * 60 * 1000; // 24h

let cachedKey: string | null = null;
let inflight: Promise<string | null> | null = null;
let scriptPreloaded = false;
let scriptReadyPromise: Promise<void> | null = null;

/** Libraries we always load. `places` for autocomplete, `geometry` for distance helpers. */
export const GOOGLE_MAPS_LIBRARIES: Libraries = ["places", "geometry"];

/** Hydrate from caches on module load. */
try {
  if (typeof sessionStorage !== "undefined") {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) cachedKey = stored;
  }
  if (!cachedKey && typeof localStorage !== "undefined") {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.key && typeof parsed.ts === "number" && Date.now() - parsed.ts < LOCAL_TTL_MS) {
        cachedKey = parsed.key as string;
        try { sessionStorage.setItem(SESSION_KEY, cachedKey); } catch { /* ignore */ }
      }
    }
  }
} catch {
  /* ignore (private mode, SSR, etc.) */
}

export function getCachedGoogleMapsKey(): string | null {
  return cachedKey;
}

/** Inject the Google Maps JS script so it's cached and parsed before any map mounts. */
export function preloadGoogleMapsScript(key?: string | null): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") return Promise.resolve();
  const k = key || cachedKey;
  if (!k) return Promise.resolve();
  // Already fully loaded
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (scriptReadyPromise) return scriptReadyPromise;

  scriptPreloaded = true;
  const SCRIPT_ID = "google-maps-script";
  scriptReadyPromise = new Promise<void>((resolve) => {
    const done = () => resolve();
    // Check if fully loaded (Map constructor exists)
    if ((window as any).google?.maps?.Map) return done();

    const callbackName = "__googleMapsCallback";
    (window as any)[callbackName] = () => {
      done();
      // Clean up if possible, but keep it in case it's called multiple times
    };

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.defer = true;
      const libs = GOOGLE_MAPS_LIBRARIES.join(",");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(k)}&libraries=${libs}&language=pt&region=AO&loading=async&callback=${callbackName}`;
      document.head.appendChild(script);
    } else {
      // If script exists but not loaded, the callback will still fire since we bound it to window
      script.addEventListener("error", done, { once: true });
    }
    
    script.addEventListener("error", done, { once: true });
    // Safety timeout — never block forever
    setTimeout(done, 6000);
  });
  return scriptReadyPromise;
}

export function loadGoogleMapsKey(): Promise<string | null> {
  if (cachedKey) {
    // Kick off script preload in the background without blocking callers.
    preloadGoogleMapsScript(cachedKey);
    return Promise.resolve(cachedKey);
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-google-maps-key");
      if (error || !data?.key) return null;
      cachedKey = data.key as string;
      try { sessionStorage.setItem(SESSION_KEY, cachedKey); } catch { /* ignore */ }
      try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ key: cachedKey, ts: Date.now() })); } catch { /* ignore */ }
      // Fire-and-forget script preload
      preloadGoogleMapsScript(cachedKey);
      return cachedKey;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Pre-warm in the background (e.g. from app boot). */
export function warmGoogleMapsKey(): void {
  if (cachedKey) {
    preloadGoogleMapsScript(cachedKey);
    return;
  }
  if (inflight) return;
  const run = () => void loadGoogleMapsKey();
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    (window as any).requestIdleCallback(run, { timeout: 800 });
  } else {
    setTimeout(run, 50);
  }
}

import { useState, useEffect } from "react";

export function useBeraMapsLoader(apiKey: string | null) {
  const [isLoaded, setIsLoaded] = useState(() => !!(typeof window !== "undefined" && (window as any).google?.maps?.Map));
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    if (isLoaded || !apiKey) return;
    
    let isMounted = true;
    preloadGoogleMapsScript(apiKey)
      .then(() => {
        if (!isMounted) return;
        if (typeof window !== "undefined" && (window as any).google?.maps?.Map) {
          setIsLoaded(true);
        } else {
          setLoadError(new Error("Google Maps API failed to load (network or adblocker)"));
        }
      })
      .catch((err) => {
        if (isMounted) setLoadError(err);
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey, isLoaded]);

  return { isLoaded, loadError };
}
